import { UserRound } from 'lucide-react'
import type { ReactNode } from 'react'

import { glyphOf, sharedWhere } from '@/features/dashboard/lib/picture'
import type { Picture } from '@/features/mitra/api/dashboard'
import { cn } from '@/lib/utils'

/**
 * A formula drawn as a picture (`check.picture`, mitra-ai spec §11): a chip
 * per figure coloured by what it measures, an aggregate as a glyph (Σ, avg,
 * #, …) before its chips, a division stacked as a fraction, and
 * "x / y × 100" as a fraction with a % badge. So a reader sees what a
 * measure is at a glance, without reading code or a sentence about it.
 *
 * LIGHT ON PURPOSE (measured in a browser, 2026-10-18): no boxed aggregates
 * and no label words -- the aggregate's word is the glyph's title -- and a
 * filter every aggregate shares is one small tag after the picture ("only
 * EPS > 0"); filters that differ sit in brackets after their own aggregate.
 *
 * The reader's own numbers (`mine`) are dashed with a person dot: theirs,
 * not a figure Mitra holds.
 *
 * NOTHING HERE IS WORDED. Every label is the engine's (`vocabulary.SHORT`);
 * the explanation sentence is the picture's accessible name, never body text.
 */

/** A figure's chip colour, by what it measures. */
const DIM_TONE: Record<string, string> = {
  money: 'bg-tone-green-soft text-tone-green',
  price: 'bg-tone-teal-soft text-tone-teal',
  shares: 'bg-tone-violet-soft text-tone-violet',
  count: 'bg-tone-violet-soft text-tone-violet',
  percent: 'bg-tone-saffron-soft text-tone-saffron',
  ratio: 'bg-tone-info-soft text-tone-info',
  text: 'bg-muted text-muted-foreground',
}

const CHIP = 'inline-flex items-center rounded-full px-2 py-1 whitespace-nowrap'

/**
 * One of the reader's own numbers: a dashed outline and a small person dot,
 * so a figure Mitra holds and a figure the reader set never look alike.
 */
export function NumberChip({ label, className }: { label: string; className?: string }) {
  return (
    <span
      data-role="pic-name"
      data-mine="true"
      className={cn(
        CHIP,
        'gap-1 border border-dashed border-foreground text-foreground',
        className,
      )}
    >
      <UserRound className="size-3" aria-hidden="true" />
      {label}
    </span>
  )
}
const SIGN = 'text-muted-foreground'

function Node({ node, inlineWhere }: { node: Picture; inlineWhere: boolean }): ReactNode {
  const sub = (child: Picture) => <Node node={child} inlineWhere={inlineWhere} />
  switch (node.t) {
    case 'name':
      if (node.mine) return <NumberChip label={node.label} />
      return (
        <span
          data-role="pic-name"
          data-dim={node.dim}
          className={cn(CHIP, DIM_TONE[node.dim] ?? DIM_TONE.ratio)}
        >
          {node.label}
        </span>
      )
    case 'num':
      return <span className="text-foreground tabular-nums">{node.value}</span>
    case 'text':
      return <span className={SIGN}>{node.value}</span>
    case 'neg':
      return (
        <span className="inline-flex items-center gap-1">
          <span className={SIGN}>−</span>
          {sub(node.of)}
        </span>
      )
    case 'op':
      return (
        <span className="inline-flex flex-wrap items-center gap-1">
          {sub(node.left)}
          <span className={SIGN}>{node.op}</span>
          {sub(node.right)}
        </span>
      )
    case 'frac':
    case 'pct':
      return (
        <span
          data-role={node.t === 'pct' ? 'pic-pct' : 'pic-frac'}
          className="inline-flex items-center gap-1"
        >
          <span className="inline-flex flex-col items-center gap-2">
            {sub(node.num)}
            <span
              aria-hidden="true"
              data-role="pic-bar"
              className="w-full min-w-10 border-t-2 border-foreground/60"
            />
            {sub(node.den)}
          </span>
          {node.t === 'pct' ? (
            <span className={cn(CHIP, 'bg-foreground text-background')}>%</span>
          ) : null}
        </span>
      )
    case 'agg':
      return (
        <span data-role="pic-agg" className="inline-flex flex-wrap items-center gap-1">
          <span data-role="pic-glyph" title={node.label} className="font-semibold text-foreground">
            {glyphOf(node)}
          </span>
          {node.of ? sub(node.of) : null}
          {node.by ? (
            <>
              <span className={SIGN}>by</span>
              {sub(node.by)}
            </>
          ) : null}
          {inlineWhere && node.where ? (
            <span data-role="pic-where" className={SIGN}>
              ({node.where})
            </span>
          ) : null}
        </span>
      )
    case 'fn':
      return (
        <span className="inline-flex flex-wrap items-center gap-1">
          <span className={SIGN}>{node.label}</span>
          {node.args.map((arg, index) => (
            <Node key={index} node={arg} inlineWhere={inlineWhere} />
          ))}
        </span>
      )
  }
}

export default function FormulaPicture({
  picture,
  label,
  size = 'sm',
  className,
}: {
  picture: Picture
  /** What it means, in a sentence: the picture's accessible name and tooltip. */
  label: string
  /** `lg` on an opened card, where the picture is drawn once and large. */
  size?: 'sm' | 'lg'
  className?: string | undefined
}) {
  const shared = sharedWhere(picture)
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-role="formula-picture"
      data-size={size}
      // THE SAME ROWS ON EVERY CARD (owner, 2026-10-20): the formula on its
      // row and the shared "only …" tag always on the next one. As one
      // wrapping run the tag sat beside a short formula on one card and
      // under a long one on its neighbour.
      className={cn(
        'inline-flex max-w-full min-w-0 flex-col items-start gap-2',
        size === 'lg' ? 'text-label-m' : 'text-label-s',
        className,
      )}
    >
      <span className="inline-flex max-w-full min-w-0 flex-wrap items-center gap-1">
        <Node node={picture} inlineWhere={shared === null} />
      </span>
      {shared ? (
        <span
          data-role="pic-only"
          className={cn(CHIP, 'border border-border-subtle text-muted-foreground')}
        >
          only {shared}
        </span>
      ) : null}
    </span>
  )
}
